import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { DateRangeValue } from '../components/DateRangeField'
import { supabase } from '../lib/supabase'
import { intervaloDoPeriodo, type Intervalo, type Periodo } from '../utils/periodo'

export type Marca = { id: string; slug: string; nome: string }

type FiltrosContextValue = {
  periodo: Periodo
  setPeriodo: (p: Periodo) => void
  customRange: DateRangeValue
  setCustomRange: (r: DateRangeValue) => void
  /** O recorte que toda consulta usa: preset ou intervalo personalizado. */
  intervalo: Intervalo
  /** '' = todas as marcas */
  marcaId: string
  setMarcaId: (id: string) => void
  marcas: Marca[]
  marcasCarregando: boolean
  marcasErro: string | null
  /** Recarrega a lista depois de criar, renomear ou desativar marca. */
  recarregarMarcas: () => void
}

const FiltrosContext = createContext<FiltrosContextValue | null>(null)

const MARCA_STORAGE_KEY = 'marketinghub-marca-id'

function lerMarcaSalva(): string {
  try {
    return localStorage.getItem(MARCA_STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

function salvarMarca(id: string) {
  try {
    if (id) localStorage.setItem(MARCA_STORAGE_KEY, id)
    else localStorage.removeItem(MARCA_STORAGE_KEY)
  } catch {
    /* modo privado / storage bloqueado: o filtro só não sobrevive ao refresh */
  }
}

/** Filtros globais da barra do topo — valem para os três painéis. */
export function FiltrosProvider({ children }: { children: ReactNode }) {
  const [periodo, setPeriodoState] = useState<Periodo>('7d')
  const [customRange, setCustomRangeState] = useState<DateRangeValue>(null)
  const [marcaId, setMarcaIdState] = useState(lerMarcaSalva)
  const [marcas, setMarcas] = useState<Marca[]>([])
  const [marcasCarregando, setMarcasCarregando] = useState(true)
  const [marcasErro, setMarcasErro] = useState<string | null>(null)
  const [versaoMarcas, setVersaoMarcas] = useState(0)
  const recarregarMarcas = useCallback(() => setVersaoMarcas((v) => v + 1), [])

  useEffect(() => {
    let cancelled = false
    supabase
      .from('marcas')
      .select('id, slug, nome')
      .eq('ativa', true)
      .order('nome')
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          setMarcasErro(error.message)
          setMarcas([])
        } else {
          const lista = (data ?? []) as Marca[]
          setMarcas(lista)
          // Marca salva que deixou de existir (ou de estar ativa) volta para "todas".
          setMarcaIdState((atual) => (atual && !lista.some((m) => m.id === atual) ? '' : atual))
        }
        setMarcasCarregando(false)
      })
    return () => {
      cancelled = true
    }
  }, [versaoMarcas])

  const setPeriodo = useCallback((p: Periodo) => {
    setCustomRangeState(null)
    setPeriodoState(p)
  }, [])

  const setCustomRange = useCallback((r: DateRangeValue) => setCustomRangeState(r), [])

  const setMarcaId = useCallback((id: string) => {
    setMarcaIdState(id)
    salvarMarca(id)
  }, [])

  const intervalo = useMemo(
    () => customRange ?? intervaloDoPeriodo(periodo),
    [customRange, periodo],
  )

  const value = useMemo(
    () => ({
      periodo,
      setPeriodo,
      customRange,
      setCustomRange,
      intervalo,
      marcaId,
      setMarcaId,
      marcas,
      marcasCarregando,
      marcasErro,
      recarregarMarcas,
    }),
    [
      periodo,
      setPeriodo,
      customRange,
      setCustomRange,
      intervalo,
      marcaId,
      setMarcaId,
      marcas,
      marcasCarregando,
      marcasErro,
      recarregarMarcas,
    ],
  )

  return <FiltrosContext.Provider value={value}>{children}</FiltrosContext.Provider>
}

export function useFiltros() {
  const ctx = useContext(FiltrosContext)
  if (!ctx) throw new Error('useFiltros fora de FiltrosProvider')
  return ctx
}
